// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import {
  AppName,
  appSelect,
  defineComponent,
  PageContainer,
  ShowInApp,
  Tabs,
  Translation,
} from '@shared/ui-kit';
import { useInstitutions } from './use-institutions';
import { Loader } from '../loader';
import { AggregationProvider } from '@shared/advisory/aggregation/providers/aggregation-provider';
import { Navigate } from 'react-router';
import { ConnectionPopup } from '../connection-popup/connection-popup';

export const Institutions = defineComponent({
  displayName: 'Institutions',
  useSetup: useInstitutions,
  View: ({
    children,
    t,
    clientId,
    clients,
    defaultTabValue,
    client,
    onTabChange,
  }) => (
    <PageContainer className="flex flex-1 flex-col gap-y-2 md:gap-y-10">
      <Tabs.Root
        baseId="client-profiles"
        defaultValue={clientId?.toString()}
        controlled
        data-testid="client-profiles-tabs"
      >
        <ShowInApp apps={AppName.Client}>
          {client === null && <Navigate to="/banks" />}
        </ShowInApp>

        <ShowInApp apps={AppName.Advisor}>
          {client === null && <Navigate to="/" />}
        </ShowInApp>

        {Array.from(clients, (client, index) => (
          <Tabs.Content
            key={`${index}-${client.id}`}
            value={client.id.toString()}
            animate={false}
            container={false}
          >
            <Tabs.Root
              baseId={`${client.id}-institutions-tabs`}
              defaultValue={defaultTabValue(client)}
              state-id={`${client.id}-institutions-tabs`}
              data-testid={`${client.id}-institutions-tabs`}
              onTabChange={onTabChange}
            >
              <div className="grid grid-cols-1 md:grid-cols-4">
                <Tabs.Scrollable className="col-span-full max-xl:order-2 xl:col-span-3">
                  <Tabs.List data-scrollable>
                    {Array.from(client.institutions, (institution, index) => (
                      <Tabs.Trigger
                        key={`${client.id}-${index}-${institution.id}`}
                        value={institution.id}
                        data-testid={`${client.id}-${institution.id}-tab-trigger`}
                        error={institution.error !== null}
                        errorMessage={t('aggregator.sync-pending', {
                          ns: 'aggregator',
                        })}
                      >
                        <Tooltip title={institution.name} placement="top">
                          <span className="line-clamp-1 block max-w-[102px] text-ellipsis">
                            {institution.name}
                          </span>
                        </Tooltip>
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                </Tabs.Scrollable>

                <div
                  id="aggregation-institutions-actions"
                  className="col-span-full flex min-w-0 items-center justify-end xl:col-span-1"
                />
              </div>

              <Tabs.Content value="none" animate={false} className="mt-4">
                <ShowInApp apps={AppName.Client}>
                  <Loader
                    status="fulfilled"
                    shouldDisplayContent={false}
                    shouldDisplayEmptyState
                    emptyStateTitle={
                      <Translation
                        i18nKey="aggregator.retail-no-banks-title"
                        ns="aggregator"
                      />
                    }
                    emptyStateDescription={
                      <Translation
                        i18nKey="aggregator.retail-no-banks-description"
                        ns="aggregator"
                      />
                    }
                    actions={<ConnectionPopup />}
                  />
                </ShowInApp>
                <ShowInApp apps={AppName.Advisor}>
                  <div data-testid="aggregation-no-shared-institutions-empty">
                    <Loader
                      status="fulfilled"
                      shouldDisplayContent={false}
                      shouldDisplayEmptyState
                      emptyStateTitle={
                        <Translation
                          i18nKey="aggregator.citec-no-shared-institutions-title"
                          ns="aggregator"
                        />
                      }
                      emptyStateDescription={
                        <Translation
                          i18nKey="aggregator.citec-no-shared-institutions-description"
                          ns="aggregator"
                        />
                      }
                    />
                  </div>
                </ShowInApp>
              </Tabs.Content>

              {Array.from(client.institutions, (institution, index) => (
                <Tabs.Content
                  value={institution.id}
                  key={`${client.id}-${index}-${institution.id}`}
                  animate={false}
                  className="mt-12"
                >
                  <AggregationProvider
                    institutionId={institution.id}
                    clientId={appSelect({
                      advisor: client.id,
                      client: undefined,
                    })}
                  >
                    {children}
                  </AggregationProvider>
                </Tabs.Content>
              ))}
            </Tabs.Root>
          </Tabs.Content>
        ))}
      </Tabs.Root>
    </PageContainer>
  ),
});
